/**
 * WhatIfPanel (spec §4) — the persuasion instrument.
 *
 * Per-dimension weight sliders, initialised to the platform's configured
 * weights. Moving a slider re-weights the composite IN THE BROWSER via the
 * vendored reweight/recomputeComposite — zero round trips, because the page
 * re-weights the platform's own scores rather than inventing new ones.
 *
 * Two honesty devices are permanent while any weight differs from the
 * configured set: the result is visually watermarked "Adjusted view — not
 * the official score" (a screenshot can never be mistaken for the official
 * number), and the reset control is always one tap away.
 */
import { areaXRayT, type AreaXRayLocale } from './copy'
import type { Contribution } from './areaScoring'

interface WhatIfPanelProps {
  contributions: Contribution[]
  weights: Record<string, number>
  onWeightChange: (slug: string, weight: number) => void
  onReset: () => void
  adjustedComposite: number | null
  platformComposite: number | null
  adjusted: boolean
  dir: 'ltr' | 'rtl'
  locale: AreaXRayLocale
}

export function WhatIfPanel({
  contributions,
  weights,
  onWeightChange,
  onReset,
  adjustedComposite,
  platformComposite,
  adjusted,
  dir,
  locale,
}: WhatIfPanelProps) {
  return (
    <section
      data-testid="what-if-panel"
      aria-label={areaXRayT('whatIfEyebrow', locale)}
      style={{ position: 'relative', border: '1px solid var(--lc-border)', borderRadius: 12, padding: 16 }}
    >
      {adjusted && (
        <div
          data-testid="adjusted-watermark"
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            transform: dir === 'rtl' ? 'rotate(6deg)' : 'rotate(-6deg)',
            fontFamily: 'var(--lc-font-ui)',
            fontWeight: 800,
            fontSize: 20,
            color: 'var(--lc-text-muted)',
            opacity: 0.35,
            border: '2px dashed var(--lc-border-strong)',
            borderRadius: 12,
            margin: 8,
            textAlign: 'center',
          }}
        >
          {areaXRayT('adjustedWatermark', locale)}
        </div>
      )}

      <p style={{ margin: 0, fontFamily: 'var(--lc-font-ui)', color: 'var(--lc-text-heading)', fontWeight: 700 }}>
        {areaXRayT('whatIfEyebrow', locale)}
      </p>
      <p style={{ margin: '4px 0 12px', fontFamily: 'var(--lc-font-ui)', color: 'var(--lc-text-muted)' }}>
        {areaXRayT('whatIfHint', locale)}
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {contributions.map((entry) => (
          <label
            key={entry.slug}
            data-testid={`weight-${entry.slug}`}
            style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 1fr) 2fr 44px', gap: 10, alignItems: 'center', fontFamily: 'var(--lc-font-ui)' }}
          >
            <span style={{ color: 'var(--lc-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {entry.label}
            </span>
            <input
              type="range"
              min={0}
              max={3}
              step={0.05}
              value={weights[entry.slug] ?? entry.weight}
              aria-label={entry.label}
              onChange={(event) => onWeightChange(entry.slug, Number(event.target.value))}
            />
            <span style={{ fontFamily: 'var(--lc-font-mono)', color: 'var(--lc-text-heading)' }}>
              ×{(weights[entry.slug] ?? entry.weight).toFixed(2).replace(/0+$/, '').replace(/\.$/, '.0')}
            </span>
          </label>
        ))}
      </div>

      <p data-testid="what-if-result" style={{ fontFamily: 'var(--lc-font-ui)', color: 'var(--lc-text-heading)', marginBottom: 8 }}>
        {areaXRayT('whatIfResult', locale, {
          from: platformComposite != null ? platformComposite.toFixed(1) : '—',
          to: adjustedComposite != null ? adjustedComposite.toFixed(1) : '—',
        })}
      </p>

      <button
        type="button"
        data-testid="reset-weights"
        onClick={onReset}
        disabled={!adjusted}
        style={{
          fontFamily: 'var(--lc-font-ui)',
          padding: '6px 12px',
          borderRadius: 8,
          border: '1px solid var(--lc-border-strong)',
          background: 'var(--lc-surface-raised)',
          color: adjusted ? 'var(--lc-action-primary)' : 'var(--lc-text-muted)',
          cursor: adjusted ? 'pointer' : 'default',
        }}
      >
        {areaXRayT('resetWeights', locale)}
      </button>
    </section>
  )
}
